/**
 * Histórico de inspeções de uma placa — no Raio-X. Cada linha abre a inspeção
 * (concluída abre em leitura). Reaproveita a listagem `/maintenance/runs`.
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import type { MaintenanceRunDto } from "@bi/types";
import { api } from "../../lib/api";
import { KIND_LABEL, RUN_STATUS } from "./labels";

export function PlateRunHistory({ plate }: { plate: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ["maintenance", "runs", "plate-history", plate],
    queryFn: () => api.get<{ rows: MaintenanceRunDto[]; total: number }>(`/maintenance/runs?plate=${encodeURIComponent(plate)}&limit=8`),
    retry: false,
    staleTime: 60_000,
  });
  const rows = data?.rows ?? [];

  return (
    <div className="card overflow-hidden scroll-mt-4" id="inspecoes">
      <div className="px-5 py-4 border-b border-surface-border flex items-center justify-between gap-3">
        <p className="text-sm font-semibold text-gray-900">Histórico de inspeções{data ? ` (${data.total})` : ""}</p>
        <Link to={`/frota/manutencao/checklists?plate=${encodeURIComponent(plate)}`} className="text-xs text-tm-700 hover:underline flex-shrink-0">
          Ver todas →
        </Link>
      </div>
      {isLoading ? (
        <p className="px-5 py-4 text-sm text-gray-400">Carregando…</p>
      ) : rows.length === 0 ? (
        <p className="px-5 py-4 text-sm text-gray-500">Nenhuma inspeção registrada para esta placa.</p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {rows.map((r) => {
            const ncs = r._count?.nonConformities ?? 0;
            return (
              <li key={r.id}>
                <Link to={`/frota/manutencao/checklists/${r.id}`} className="flex items-center justify-between gap-3 px-5 py-3 hover:bg-surface-muted">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900">{new Date(r.startedAt).toLocaleDateString("pt-BR")}</div>
                    <div className="text-xs text-gray-500 truncate">{r.template?.name ?? KIND_LABEL[r.kind]}{r.driverName ? ` · ${r.driverName}` : ""}</div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={RUN_STATUS[r.status].badge}>{RUN_STATUS[r.status].label}</span>
                    {r.status === "completed" && (ncs > 0 ? <span className="badge-red">{ncs} NC{ncs > 1 ? "s" : ""}</span> : <span className="text-xs text-gray-400">Sem problemas</span>)}
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
